import { getCurrentInstance, onBeforeUnmount, onMounted } from 'vue'

/**
 * Prevents external focus-trapping libraries (e.g. Bootstrap 5 modals) from
 * stealing focus away from elements inside this custom element's shadow root.
 *
 * Bootstrap modals attach a `focusin` listener on `document` that pulls focus
 * back whenever it leaves the modal subtree. Because our custom element lives
 * outside the modal in the DOM, every focus event that escapes the shadow root
 * is treated as a "focus escape" and gets redirected back into the modal.
 *
 * Fix: intercept `focusin` at the shadow root boundary in the **bubble phase**
 * and stop its propagation. The event still reaches every internal element
 * normally (capture → target → bubble up through the shadow tree), but is
 * stopped at the shadow root before it can re-target and continue to `document`.
 *
 * Must be called inside a `.ce.vue` root component's `<script setup>`.
 */
export function useFocusTrapBypass() {
    let shadowRoot: ShadowRoot | null = null

    const stopFocusPropagation = (event: Event) => {
        event.stopPropagation()
    }

    onMounted(() => {
        const instance = getCurrentInstance()
        const rootNode = instance?.proxy?.$el?.getRootNode()

        if (!(rootNode instanceof ShadowRoot)) {
            return
        }

        shadowRoot = rootNode
        shadowRoot.addEventListener('focusin', stopFocusPropagation)
    })

    onBeforeUnmount(() => {
        if (!shadowRoot) {
            return
        }

        shadowRoot.removeEventListener('focusin', stopFocusPropagation)
        shadowRoot = null
    })
}
